import { type RequestPayload, router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Accordion, Button, Group, Stack, Switch, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconChevronsDown, IconChevronsUp, IconDownload } from "@tabler/icons-react";
import { useState } from "react";
import type { Coach } from "../../../models/coach";
import { CompetenceCategory } from "../../../models/enums/competenceCategory";
import { gradeTranslationsType3, gradeTranslationsType4 } from "../../../models/enums/grade";
import type { Observation } from "../../../models/observation";
import type { EducationReport, EducationReportData } from "../../../models/reports/educationReport";
import type { EducationReportTranslations } from "../../../models/reports/educationReportTranslations";
import {
  getAcademicGradeFields,
  getCoachSupportFields,
  getDocumentationFields,
  getMethodologicalFields,
  getPastSemesterFields,
  getProfessionalFields,
  getReceivedTrainingFields,
  getSelfFields,
  getSocialFields,
} from "../educationReport/GradingFields";
import { ObservationImport } from "../educationReport/ObservationImport";
import { RemarkedGradingSection } from "../educationReport/RemarkedGradingSection";
import { BaseReportForm } from "./BaseReportForm";
import type { CommonReportFormType } from "./CommonReportForm";
import { MeteredTextarea } from "./MeteredTextarea";

type EducationReportFormProps = {
  educationReport: EducationReport;
  saveButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
  translations: EducationReportTranslations;
  observations?: Observation[];
  coaches: Coach[];
};

export type EducationReportFormType = CommonReportFormType & {
  data: EducationReportData;
};

type FormErrors = {
  title?: string[];
  status?: string[];
  data?: string[];
};

export function EducationReportForm({ educationReport, saveButtonLabel, onSubmit, translations, coaches }: EducationReportFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;
  const maxLength = educationReport.report_configuration.data.max_remark_chars;
  const lengthValidation = (value: string, fraction = 1) => {
    const fieldMaxLength = maxLength / fraction;
    return value?.length > fieldMaxLength ? `zu lange (max ${fieldMaxLength} Zeichen)` : null;
  };

  const form = useForm<EducationReportFormType>({
    mode: "uncontrolled",
    initialValues: {
      title: educationReport.title,
      status: educationReport.status,
      responsible_coach_id: educationReport.coach.id.toString(),
      data: {
        ...educationReport.data,
        discussed_at: educationReport.data.discussed_at ?? null,
      },
    },
    validateInputOnChange: true,
    validate: {
      data: {
        professional_competence: {
          remarks: (value) => lengthValidation(value),
        },
        methodological_competence: {
          remarks: (value) => lengthValidation(value),
        },
        social_competence: {
          remarks: (value) => lengthValidation(value),
        },
        self_competence: {
          remarks: (value) => lengthValidation(value),
        },
        learn_documentation: {
          remarks: (value) => lengthValidation(value, 2),
        },
        academic_grades: {
          remarks: (value) => lengthValidation(value, 2),
        },
        apprentice_assessment: {
          received_training: {
            remarks: (value) => lengthValidation(value),
          },
          coach_support: {
            remarks: (value) => lengthValidation(value),
          },
        },
        past_semester_review: {
          remarks: (value) => lengthValidation(value),
        },
        next_semester_goals: {
          company_goals: (value) => lengthValidation(value),
          educational_goals: (value) => lengthValidation(value),
          uek_goals: (value) => lengthValidation(value),
          professional_competence: (value) => lengthValidation(value),
          methodological_competence: (value) => lengthValidation(value),
          social_competence: (value) => lengthValidation(value),
          self_competence: (value) => lengthValidation(value),
        },
        course_agreements: (value) => lengthValidation(value),
        diverse: (value) => lengthValidation(value),
      },
    },
  });

  const openImportModal = (competence: CompetenceCategory, prevData: string, key: string) => {
    const modalId = modals.open({
      size: "70%",
      title: "Beobachtungen importieren",
      children: <ObservationImport loading />,
    });
    router.reload({
      only: ["observations"],
      data: { competence_category: competence },
      onSuccess: (page) => {
        modals.updateModal({
          modalId,
          children: (
            <ObservationImport
              onClose={(texts) => {
                updateCompetenceField(texts, prevData, key);
                modals.close(modalId);
              }}
              observations={page.props.observations as Observation[]}
            />
          ),
        });
      },
    });
  };

  const updateCompetenceField = (texts: string[], prevData: string, competenceFormKey: string) => {
    let text = texts.join("\n");
    text += `\n${prevData}`;
    form.setFieldValue(competenceFormKey, text);
  };

  const professionalFields = getProfessionalFields(translations, educationReport.self_assessment?.professional_competence);
  const methodologicalFields = getMethodologicalFields(translations, educationReport.self_assessment?.methodological_competence);
  const socialFields = getSocialFields(translations, educationReport.self_assessment?.social_competence);
  const selfFields = getSelfFields(translations, educationReport.self_assessment?.self_competence);
  const documentationFields = getDocumentationFields(translations);
  const academicGradeFields = getAcademicGradeFields(translations);
  const receivedTrainingFields = getReceivedTrainingFields(
    translations,
    educationReport.self_assessment.apprentice_assessment.received_training,
  );
  const coachSupportFields = getCoachSupportFields(educationReport.self_assessment.apprentice_assessment.coach_support);
  const pastSemesterFields = getPastSemesterFields(translations);

  const copyApprenticeEvaluation = () => {
    for (const field of receivedTrainingFields) {
      form.setFieldValue(`data.apprentice_assessment.received_training.${field.key}`, (prev) => field.selfAssessment ?? prev);
    }

    for (const field of coachSupportFields) {
      form.setFieldValue(`data.apprentice_assessment.coach_support.${field.key}`, (prev) => field.selfAssessment ?? prev);
    }

    form.setFieldValue(
      "data.apprentice_assessment.received_training.remarks",
      (prev) => educationReport.self_assessment.apprentice_assessment.received_training.remarks ?? prev,
    );
    form.setFieldValue(
      "data.apprentice_assessment.coach_support.remarks",
      (prev) => educationReport.self_assessment.apprentice_assessment.coach_support.remarks ?? prev,
    );
  };

  const accordionFields = [
    {
      title: translations.professional_competence.title,
      key: "professional_competence",
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
          fields={professionalFields}
          displaySelfAssessment={displaySelfAssessment}
          remarksSelfAssessment={educationReport.self_assessment.professional_competence.remarks}
          onObeservationLookup={() =>
            openImportModal(CompetenceCategory.PROFESSIONAL, form.getValues().data.professional_competence.remarks, `data.${key}.remarks`)
          }
        />
      ),
    },
    {
      title: translations.methodological_competence.title,
      key: "methodological_competence",
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
          fields={methodologicalFields}
          displaySelfAssessment={displaySelfAssessment}
          remarksSelfAssessment={educationReport.self_assessment.methodological_competence.remarks}
          onObeservationLookup={() =>
            openImportModal(
              CompetenceCategory.METHODOLOGICAL,
              form.getValues().data.methodological_competence.remarks,
              `data.${key}.remarks`,
            )
          }
        />
      ),
    },
    {
      title: translations.social_competence.title,
      key: "social_competence",
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
          fields={socialFields}
          displaySelfAssessment={displaySelfAssessment}
          remarksSelfAssessment={educationReport.self_assessment.social_competence.remarks}
          onObeservationLookup={() =>
            openImportModal(CompetenceCategory.SOCIAL, form.getValues().data.social_competence.remarks, `data.${key}.remarks`)
          }
        />
      ),
    },
    {
      title: translations.self_competence.title,
      key: "self_competence",
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
          fields={selfFields}
          displaySelfAssessment={displaySelfAssessment}
          remarksSelfAssessment={educationReport.self_assessment.self_competence.remarks}
          onObeservationLookup={() =>
            openImportModal(CompetenceCategory.SELF, form.getValues().data.self_competence.remarks, `data.${key}.remarks`)
          }
        />
      ),
    },
    {
      title: translations.learn_documentation.title,
      key: "learn_documentation",
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          remarksKey="remarks"
          remarksMaxLength={maxLength / 2}
          fields={documentationFields}
        />
      ),
    },
    {
      title: translations.academic_grades.title,
      key: "academic_grades",
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          remarksKey="remarks"
          remarksMaxLength={maxLength / 2}
          fields={academicGradeFields}
        />
      ),
    },
    {
      title: translations.apprentice_assessment.title,
      key: "apprentice_assessment",
      render: (baseNumber: number, key: string) => (
        <Stack>
          {displaySelfAssessment && (
            <Button onClick={copyApprenticeEvaluation} ml="auto" size="sm" leftSection={<IconDownload />} variant="light">
              Beurteilung übernehmen
            </Button>
          )}
          <RemarkedGradingSection
            title={`${baseNumber}.1 ${translations.apprentice_assessment.received_training.title}`}
            disableNumbering
            path={`${key}.received_training`}
            baseNumber={baseNumber}
            form={form}
            remarksKey="remarks"
            remarksMaxLength={maxLength}
            fields={receivedTrainingFields}
            gradeTranslations={gradeTranslationsType3}
            displaySelfAssessment={displaySelfAssessment}
            remarksSelfAssessment={educationReport.self_assessment.apprentice_assessment.received_training.remarks}
          />
          <RemarkedGradingSection
            title={`${baseNumber}.2 ${translations.apprentice_assessment.coach_support.title}`}
            disableNumbering
            path={`${key}.coach_support`}
            baseNumber={baseNumber}
            form={form}
            remarksKey="remarks"
            remarksMaxLength={maxLength}
            fields={coachSupportFields}
            gradeTranslations={gradeTranslationsType3}
            displaySelfAssessment={displaySelfAssessment}
            remarksSelfAssessment={educationReport.self_assessment.apprentice_assessment.coach_support.remarks}
          />
        </Stack>
      ),
    },
    {
      disabled: !educationReport.report_configuration.data.enable_point_eight,
      title: translations.past_semester_review.title,
      key: "past_semester_review",
      render: (baseNumber: number, key: string) => (
        <RemarkedGradingSection
          path={key}
          baseNumber={baseNumber}
          form={form}
          remarksKey="remarks"
          remarksMaxLength={maxLength}
          fields={pastSemesterFields}
          gradeTranslations={gradeTranslationsType4}
          disableNumbering
        />
      ),
    },
    {
      title: translations.next_semester_goals.title,
      key: "next_semester_goals",
      render: (_baseNumber: number, key: string) => (
        <Stack>
          <MeteredTextarea<EducationReportFormType>
            form={form}
            path={`data.${key}.company_goals`}
            label={translations.next_semester_goals.company_goals.title}
            maxLength={maxLength}
          />
          <MeteredTextarea<EducationReportFormType>
            form={form}
            path={`data.${key}.educational_goals`}
            label={translations.next_semester_goals.educational_goals.title}
            maxLength={maxLength}
          />
          <MeteredTextarea<EducationReportFormType>
            form={form}
            path={`data.${key}.uek_goals`}
            label={translations.next_semester_goals.uek_goals.title}
            maxLength={maxLength}
          />
          <MeteredTextarea<EducationReportFormType>
            form={form}
            path={`data.${key}.professional_competence`}
            label={translations.next_semester_goals.professional_competence.title}
            maxLength={maxLength}
          />
          <MeteredTextarea<EducationReportFormType>
            form={form}
            path={`data.${key}.methodological_competence`}
            label={translations.next_semester_goals.methodological_competence.title}
            maxLength={maxLength}
          />
          <MeteredTextarea<EducationReportFormType>
            form={form}
            path={`data.${key}.social_competence`}
            label={translations.next_semester_goals.social_competence.title}
            maxLength={maxLength}
          />
          <MeteredTextarea<EducationReportFormType>
            form={form}
            path={`data.${key}.self_competence`}
            label={translations.next_semester_goals.self_competence.title}
            maxLength={maxLength}
          />
        </Stack>
      ),
    },
    {
      title: translations.course_agreements.title,
      key: "course_agreements",
      render: (_baseNumber: number, key: string) => (
        <MeteredTextarea<EducationReportFormType> form={form} path={`data.${key}`} label="" maxLength={maxLength} />
      ),
    },
    {
      title: translations.diverse.title,
      key: "diverse",
      render: (_baseNumber: number, key: string) => (
        <MeteredTextarea<EducationReportFormType> form={form} path={`data.${key}`} label="" maxLength={maxLength} />
      ),
    },
    {
      title: translations.discussed_at.title,
      key: "discussed_at",
      render: (_baseNumber: number, key: string) => (
        <DatePickerInput clearable valueFormat="DD.MM.YYYY" key={form.key(`data.${key}`)} {...form.getInputProps(`data.${key}`)} />
      ),
    },
  ];
  const accordionKeys = accordionFields
    .filter((f) => (f.key === "past_semester_review" ? educationReport.report_configuration.data.enable_point_eight : true))
    .map((f) => f.key);
  const [openedAccordion, setOpenAccordion] = useState(accordionKeys);
  const anyAccordionsOpen = openedAccordion.length > 0;

  const [displaySelfAssessment, setDisplaySelfAssessment] = useState(false);

  return (
    <BaseReportForm
      saveButtonDisabled={!form.isValid()}
      form={form}
      report={educationReport}
      errors={errors}
      coaches={coaches}
      saveButtonLabel={saveButtonLabel}
      onSubmit={form.onSubmit(onSubmit)}
    >
      <Group>
        <Button
          leftSection={anyAccordionsOpen ? <IconChevronsUp /> : <IconChevronsDown />}
          color="zli-blue"
          mr="auto"
          variant="subtle"
          onClick={() => setOpenAccordion(anyAccordionsOpen ? [] : accordionKeys)}
        >
          {anyAccordionsOpen ? "Alle einklappen" : "Alle aufklappen"}
        </Button>

        <Switch
          checked={displaySelfAssessment}
          onChange={(event) => setDisplaySelfAssessment(event.currentTarget.checked)}
          color="zli-blue"
          size="md"
          label="Selbsteinschätzung"
        />
      </Group>
      <Accordion multiple value={openedAccordion} onChange={setOpenAccordion}>
        {accordionFields.map((field, index) => (
          <Accordion.Item key={field.key} value={field.key}>
            <Accordion.Control disabled={field.disabled}>
              <Title order={4}>
                {index + 1}. {field.title}
              </Title>
            </Accordion.Control>
            <Accordion.Panel>{field.render(index + 1, field.key)}</Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </BaseReportForm>
  );
}
